import {
  allowedTextPositions,
  bookPlan,
  type BookPlanId,
  type SpreadLayoutId,
  type TextPosition,
} from '@kitup/book-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import type { BookDto } from '../api/books.js';
import { spreadAspect } from './artwork-aspect.js';
import { ApiError, userErrorMessage } from '../api/errors.js';
import { illustrationsApi, type IllustrationSceneDto } from '../api/illustrations.js';
import { monetizationApi, redirectToCheckout } from '../api/monetization.js';
import {
  inspectPhoto,
  PHOTO_ACCEPT,
  photoUploadMime,
  uploadPhoto,
  type PhotoIssue,
} from '../api/uploads.js';
import { locationsApi } from '../api/library.js';
import { storyApi } from '../api/story.js';
import { Link } from 'react-router';
import { EmptyState } from '../components/EmptyState.js';
import { ImageViewer } from '../components/ImageViewer.js';
import { GenerationFailed } from '../components/GenerationFailed.js';
import { formatPrice } from '@kitup/brand';
import { useBrand } from '../brand/context.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';
import { planFeatures } from '../monetization/plans.js';
import { PaymentTerms } from './PaymentTerms.js';
import { ReplacementPackModal } from './ReplacementPackModal.js';

const LAYOUTS: readonly SpreadLayoutId[] = ['split', 'full-bleed', 'band'];
type SceneChangeRequest = {
  readonly mode: 'edit' | 'replace';
  readonly sceneId: string;
  readonly guidance: string;
  readonly referenceAssetId?: string;
};
type RegenerationReference = {
  readonly assetId: string;
  readonly name: string;
  readonly url: string;
};

export function IllustrationsStep({
  book,
  billingExempt = false,
  paymentConfirming = false,
}: {
  book: BookDto;
  billingExempt?: boolean;
  /**
   * Back from the provider's checkout, with the payment not yet confirmed. The book still
   * reads unpaid for those seconds, and the payment dialog opened over a payment just made.
   */
  paymentConfirming?: boolean;
}): JSX.Element {
  const { t, locale } = useI18n();
  const brand = useBrand();
  const legal = useLegal();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [regenerationGuidance, setRegenerationGuidance] = useState<Record<string, string>>({});
  const [regenerationReferences, setRegenerationReferences] = useState<
    Record<string, RegenerationReference>
  >({});
  const [referenceUploading, setReferenceUploading] = useState<string | null>(null);
  const [referenceIssues, setReferenceIssues] = useState<Record<string, PhotoIssue | 'failed'>>({});
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [replacementRequest, setReplacementRequest] = useState<SceneChangeRequest | null>(null);
  /*
   * Closed until asked for. Opening on its own the moment the free spread was ready, it
   * covered the very spread it was selling — "see it before you pay" with the picture
   * behind a dialog. The banner above the spread holds the Pay button.
   */
  const [paymentDismissed, setPaymentDismissed] = useState(true);
  const query = useQuery({
    queryKey: ['book-illustrations', book.id],
    queryFn: () => illustrationsApi.list(book.id),
    refetchInterval: (current) => {
      const progress = current.state.data?.progress;
      const promptActive = current.state.data?.items.some(
        (scene) => scene.promptStatus === 'queued' || scene.promptStatus === 'running',
      );
      return (progress && progress.queued + progress.generating > 0) || promptActive
        ? 3_000
        : false;
    },
  });

  /*
   * The server finishes a step's work on the step's own endpoint (it syncs finished
   * generations as it answers), so the book the sidebar reads learns of it only when
   * refetched. Without this a finished step stayed "Empty" or "In progress" until reload.
   * Only when what the step holds changes, not on every poll while it generates.
   */
  const stepState = query.data
    ? query.data.items
        .map((scene) => `${scene.status}:${scene.imageStale}:${scene.imageAssetId}`)
        .join(',')
    : '';
  useEffect(() => {
    if (stepState) void queryClient.invalidateQueries({ queryKey: ['book', book.id] });
  }, [book.id, stepState, queryClient]);
  const locations = useQuery({
    queryKey: ['book-locations', book.id],
    queryFn: () => locationsApi.forBook(book.id),
  });
  const payment = useMutation({
    mutationFn: (promoCode?: string) =>
      monetizationApi.pay(book.id, legal.documentVersionId, promoCode),
    onSuccess: (result) => redirectToCheckout(result),
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['book-illustrations', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book-scenes', book.id] }),
    ]);
  };
  const run = (key: string, action: Promise<unknown>, onSuccess?: () => void): void => {
    setPending(key);
    setError(null);
    void action
      .then(() => {
        onSuccess?.();
        return refresh();
      })
      .catch((reason: unknown) => setError(userErrorMessage(reason, t)))
      .finally(() => setPending(null));
  };
  const submitSceneChange = (request: SceneChangeRequest) => {
    setPending(`${request.mode}-${request.sceneId}`);
    setError(null);
    const action =
      request.mode === 'edit'
        ? illustrationsApi.edit(request.sceneId, request.guidance, request.referenceAssetId)
        : illustrationsApi.regenerate(request.sceneId);
    void action
      .then(() => {
        if (request.mode === 'edit') {
          setRegenerationGuidance((current) => {
            const next = { ...current };
            delete next[request.sceneId];
            return next;
          });
          setRegenerationReferences((current) => {
            const next = { ...current };
            delete next[request.sceneId];
            return next;
          });
          setReferenceIssues((current) => {
            const next = { ...current };
            delete next[request.sceneId];
            return next;
          });
        }
        return refresh();
      })
      .catch((reason: unknown) => {
        if (reason instanceof ApiError && reason.code === 'plan_limit_exceeded') {
          setReplacementRequest(request);
          return;
        }
        setError(userErrorMessage(reason, t));
      })
      .finally(() => setPending(null));
  };
  const changeScene = (scene: IllustrationSceneDto, mode: 'edit' | 'replace'): void => {
    const reference = regenerationReferences[scene.id];
    const request = {
      mode,
      sceneId: scene.id,
      guidance: mode === 'edit' ? (regenerationGuidance[scene.id]?.trim() ?? '') : '',
      ...(mode === 'edit' && reference ? { referenceAssetId: reference.assetId } : {}),
    } satisfies SceneChangeRequest;
    if (mode === 'edit' && !request.guidance) return;
    if (
      !billingExempt &&
      !query.data?.replacements.unlimited &&
      scene.variants.length > 0 &&
      query.data?.replacements.remaining === 0
    ) {
      setReplacementRequest(request);
      return;
    }
    // Replacing draws the spread again from scratch and spends a redo on the first click;
    // the picture it replaces stays among the variants, but nothing said so before.
    if (
      mode === 'replace' &&
      scene.variants.length > 0 &&
      !window.confirm(
        billingExempt || query.data?.replacements.unlimited
          ? t.illustrations.replaceConfirmUnlimited
          : t.illustrations.replaceConfirm(query.data?.replacements.remaining ?? 0),
      )
    ) {
      return;
    }
    submitSceneChange(request);
  };
  const uploadRegenerationReference = async (sceneId: string, file: File): Promise<void> => {
    const checked = inspectPhoto({ mime: photoUploadMime(file), bytes: file.size });
    const blockingIssue = checked.blocking[0];
    if (blockingIssue) {
      setReferenceIssues((current) => ({ ...current, [sceneId]: blockingIssue }));
      return;
    }
    setReferenceUploading(sceneId);
    setReferenceIssues((current) => {
      const next = { ...current };
      delete next[sceneId];
      return next;
    });
    try {
      const asset = await uploadPhoto(file, 'moodboard');
      setRegenerationReferences((current) => ({
        ...current,
        [sceneId]: { assetId: asset.id, name: file.name, url: asset.url },
      }));
      const measured = inspectPhoto({
        mime: asset.mime,
        bytes: asset.bytes,
        width: asset.width,
        height: asset.height,
      });
      const warning = measured.warnings[0];
      if (warning) setReferenceIssues((current) => ({ ...current, [sceneId]: warning }));
    } catch {
      setReferenceIssues((current) => ({ ...current, [sceneId]: 'failed' }));
    } finally {
      setReferenceUploading(null);
    }
  };
  const replacementPack = useMutation({
    mutationFn: () =>
      monetizationApi.buyImageReplacementPack(book.id, legal.documentVersionId, 'illustrations'),
    onSuccess: (result) => redirectToCheckout(result),
  });

  if (query.isPending) return <p>{t.common.loading}</p>;
  if (query.isError) return <p role="alert">{query.error.message}</p>;
  const { progress, replacements } = query.data;
  const paid = Boolean(book.paidAt) || billingExempt;
  const generationActive = progress.queued + progress.generating > 0;
  const promptsReady = query.data.items.every((scene) => scene.promptStatus === 'ready');
  const preview = query.data.items[0];
  const paymentReady = !paid && !paymentConfirming && preview?.status === 'ready';
  const visibleItems = paid ? query.data.items : query.data.items.slice(0, 1);

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t.illustrations.title}</h1>
        </div>
        {/* Once every spread is drawn there is nothing left for "Generate all" to do. */}
        {paid && progress.ready < progress.total ? (
          <button
            type="button"
            className="kitup-primary-button"
            disabled={pending !== null || generationActive || !promptsReady || progress.total === 0}
            onClick={() => run('all', illustrationsApi.generateAll(book.id))}
          >
            {pending === 'all' ? t.illustrations.generatingAll : t.illustrations.generateAll}
          </button>
        ) : null}
      </div>

      {!paid ? (
        <div className="rounded-2xl border border-[var(--kitup-color-border)] bg-[var(--kitup-color-surface-soft)] p-4">
          {/* Once the spread exists the banner stops saying it is being made. */}
          <p className="font-semibold">
            {paymentReady ? t.monetization.preview.readyTitle : t.monetization.preview.title}
          </p>
          <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">
            {paymentReady
              ? t.monetization.preview.readyText(
                  Math.max(0, query.data.items.length - 1),
                  t.monetization.plans[book.plan].name,
                )
              : t.monetization.preview.text}
          </p>
          {paymentReady && paymentDismissed ? (
            <button
              type="button"
              className="kitup-primary-button mt-3"
              onClick={() => setPaymentDismissed(false)}
            >
              {t.monetization.payment.action(
                formatPrice(brand.planPrices[book.plan], brand, locale),
              )}
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        role="status"
        className="rounded-[var(--kitup-radius-sm)] bg-[var(--kitup-color-surface-soft)] p-3 text-sm"
      >
        {t.illustrations.progress(progress.ready, progress.total)}
        {progress.queued + progress.generating > 0
          ? ` · ${t.illustrations.eta(progress.estimatedSeconds)}`
          : ''}
        {` · ${
          replacements.unlimited || billingExempt
            ? t.admin.accounts.replacementUnlimited
            : t.monetization.replacementsLeft(
                replacements.remaining,
                replacements.included + replacements.extra,
              )
        }`}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {query.data.items.length === 0 ? (
        <EmptyState
          title={t.illustrations.empty}
          action={
            <Link to="?step=story" className="kitup-secondary-button">
              {t.builder.goToStep(t.builder.steps.story)}
            </Link>
          }
        />
      ) : null}

      <ul className="grid items-start gap-5 md:grid-cols-2">
        {visibleItems.map((scene) => (
          <li key={scene.id} className="kitup-card overflow-hidden">
            <ScenePreview scene={scene} aspect={spreadAspect(book.attributes, scene)} />
            <div className="grid gap-4 p-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold">{t.story.manual.spread(scene.index + 1)}</h2>
                <div className="flex items-center gap-2">
                  <span className={`kitup-status kitup-status-${scene.status}`}>
                    {t.illustrations.statuses[scene.status]}
                  </span>
                  <button
                    type="button"
                    // A labelled button: a lone pencil did not say that redo, layout and text
                    // position all live behind it.
                    className="flex h-9 items-center gap-1.5 rounded-full border border-[var(--kitup-color-border)] px-3 text-sm font-medium text-[var(--kitup-color-muted)] transition hover:border-[var(--kitup-color-accent)] hover:text-[var(--kitup-color-accent)] aria-expanded:border-[var(--kitup-color-accent)] aria-expanded:bg-[var(--kitup-color-accent)]/10 aria-expanded:text-[var(--kitup-color-accent)]"
                    aria-expanded={editingSceneId === scene.id}
                    aria-controls={`illustration-controls-${scene.id}`}
                    aria-label={
                      editingSceneId === scene.id
                        ? t.illustrations.closeControls(scene.index + 1)
                        : t.illustrations.editControls(scene.index + 1)
                    }
                    title={
                      editingSceneId === scene.id
                        ? t.illustrations.closeControls(scene.index + 1)
                        : t.illustrations.editControls(scene.index + 1)
                    }
                    onClick={() =>
                      setEditingSceneId((current) => (current === scene.id ? null : scene.id))
                    }
                  >
                    <PencilIcon />
                    <span aria-hidden="true">
                      {editingSceneId === scene.id ? t.common.close : t.illustrations.change}
                    </span>
                  </button>
                </div>
              </div>
              {/* The words on this spread, so a picture can be judged against them here. */}
              {scene.textBlocks.some((block) => block.text.trim()) ? (
                <p className="line-clamp-2 text-sm text-[var(--kitup-color-muted)]">
                  {scene.textBlocks
                    .map((block) => block.text.trim())
                    .filter(Boolean)
                    .join(' ')}
                </p>
              ) : null}
              {scene.imageStale ? (
                <p className="text-sm text-amber-800">{t.illustrations.stale}</p>
              ) : null}
              {scene.promptStatus === 'queued' || scene.promptStatus === 'running' ? (
                <p role="status" className="text-sm text-[var(--kitup-color-muted)]">
                  {t.illustrations.promptUpdating}
                </p>
              ) : null}
              {scene.promptStatus === 'failed' ? (
                <p role="alert" className="text-sm text-red-700">
                  {scene.promptError || t.illustrations.promptFailed}
                </p>
              ) : null}
              <div
                id={`illustration-controls-${scene.id}`}
                className={
                  editingSceneId === scene.id
                    ? 'grid gap-4 border-t border-[var(--kitup-color-border)] pt-4'
                    : 'hidden'
                }
              >
                {/*
                  Before payment the settings below are shown but locked. They looked exactly
                  like working ones, so a tap did nothing and said nothing: say it up front.
                */}
                {paid ? null : (
                  <p role="note" className="text-sm text-[var(--kitup-color-muted)]">
                    {t.illustrations.settingsLocked}
                  </p>
                )}
                {paid && scene.variants.length > 0 ? (
                  <div className="grid gap-2">
                    <label className="grid gap-1.5 text-sm">
                      <span className="font-medium">
                        {t.illustrations.regenerationGuidance.label}
                      </span>
                      <textarea
                        className="kitup-input min-h-24 resize-y"
                        value={regenerationGuidance[scene.id] ?? ''}
                        maxLength={1_000}
                        disabled={pending !== null}
                        placeholder={t.illustrations.regenerationGuidance.placeholder}
                        onChange={(event) =>
                          setRegenerationGuidance((current) => ({
                            ...current,
                            [scene.id]: event.target.value,
                          }))
                        }
                      />
                      <span className="text-xs text-[var(--kitup-color-muted)]">
                        {t.illustrations.regenerationGuidance.hint}
                      </span>
                    </label>
                    <label className="grid gap-1.5 text-sm">
                      <span className="font-medium">
                        {t.illustrations.regenerationReference.label}
                      </span>
                      <input
                        type="file"
                        accept={PHOTO_ACCEPT}
                        aria-label={t.illustrations.regenerationReference.label}
                        className="text-xs"
                        disabled={pending !== null || referenceUploading !== null}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          void uploadRegenerationReference(scene.id, file);
                          event.target.value = '';
                        }}
                      />
                      <span className="text-xs text-[var(--kitup-color-muted)]">
                        {t.illustrations.regenerationReference.hint}
                      </span>
                    </label>
                    {referenceUploading === scene.id ? (
                      <p role="status" className="text-sm">
                        {t.illustrations.regenerationReference.uploading}
                      </p>
                    ) : null}
                    {regenerationReferences[scene.id] ? (
                      <div className="flex items-center gap-3 rounded-xl border border-[var(--kitup-color-border)] p-2">
                        <img
                          src={regenerationReferences[scene.id]!.url}
                          alt=""
                          className="h-16 w-16 rounded-lg object-contain"
                        />
                        <span className="min-w-0 flex-1 truncate text-sm">
                          {regenerationReferences[scene.id]!.name}
                        </span>
                        <button
                          type="button"
                          className="kitup-quiet-button text-xs"
                          disabled={pending !== null}
                          onClick={() => {
                            setRegenerationReferences((current) => {
                              const next = { ...current };
                              delete next[scene.id];
                              return next;
                            });
                            setReferenceIssues((current) => {
                              const next = { ...current };
                              delete next[scene.id];
                              return next;
                            });
                          }}
                        >
                          {t.illustrations.regenerationReference.remove}
                        </button>
                      </div>
                    ) : null}
                    {referenceIssues[scene.id] ? (
                      <p role="status" className="text-sm text-amber-800">
                        {t.illustrations.regenerationReference.issues[referenceIssues[scene.id]!]}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className="kitup-primary-button justify-self-start"
                      disabled={
                        pending !== null ||
                        scene.promptStatus !== 'ready' ||
                        scene.status === 'queued' ||
                        scene.status === 'generating' ||
                        referenceUploading !== null ||
                        !(regenerationGuidance[scene.id]?.trim().length ?? 0)
                      }
                      onClick={() => changeScene(scene, 'edit')}
                    >
                      {t.illustrations.editCurrent}
                    </button>
                  </div>
                ) : null}
                {scene.lastError ? (
                  <div className="grid gap-2">
                    <GenerationFailed
                      error={scene.lastError}
                      generationId={scene.lastGenerationId}
                    />
                    <button
                      type="button"
                      className="kitup-secondary-button justify-self-start"
                      disabled={pending !== null || scene.promptStatus !== 'ready'}
                      onClick={() => changeScene(scene, 'replace')}
                    >
                      {t.illustrations.retry}
                    </button>
                  </div>
                ) : paid ? (
                  <div className="grid justify-items-start gap-1.5">
                    <button
                      type="button"
                      className="kitup-secondary-button"
                      disabled={
                        pending !== null ||
                        scene.promptStatus !== 'ready' ||
                        scene.status === 'queued' ||
                        scene.status === 'generating'
                      }
                      onClick={() => changeScene(scene, 'replace')}
                    >
                      {scene.variants.length > 0
                        ? t.illustrations.regenerate
                        : t.illustrations.generate}
                    </button>
                    {scene.variants.length > 0 ? (
                      <span className="text-xs text-[var(--kitup-color-muted)]">
                        {t.illustrations.replaceHint}
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {/* Set apart from redrawing the picture above: arranging the page uses no attempt. */}
                <h3 className="border-t border-[var(--kitup-color-border)] pt-4 text-sm font-semibold">
                  {t.illustrations.pageSettings}
                </h3>
                <label className="grid gap-1 text-sm">
                  <span>{t.illustrations.layout}</span>
                  <select
                    disabled={!paid}
                    value={scene.layoutId ?? book.attributes.layout.default}
                    onChange={(event) =>
                      run(
                        `layout-${scene.id}`,
                        storyApi.updateScene(scene.id, {
                          layoutId: event.target.value as SpreadLayoutId,
                        }),
                      )
                    }
                  >
                    {LAYOUTS.map((layout) => (
                      <option key={layout} value={layout}>
                        {t.builder.layoutNames[layout]}
                      </option>
                    ))}
                  </select>
                </label>

                {/* A dropdown with only "No saved location" in it is a choice with nothing to choose. */}
                {(locations.data?.items.length ?? 0) > 0 || scene.locationId ? (
                  <label className="grid gap-1 text-sm">
                    <span>{t.illustrations.location}</span>
                    <select
                      disabled={!paid}
                      value={scene.locationId ?? ''}
                      onChange={(event) =>
                        run(
                          `location-${scene.id}`,
                          storyApi.updateScene(scene.id, {
                            locationId: event.target.value || null,
                          }),
                        )
                      }
                    >
                      <option value="">{t.illustrations.noLocation}</option>
                      {(locations.data?.items ?? []).map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                {scene.textBlocks.map((block, index) => {
                  const layout = scene.layoutId ?? book.attributes.layout.default;
                  return (
                    <label key={block.slotId} className="grid gap-1 text-sm">
                      <span>
                        {t.illustrations.textPosition(index + 1, scene.textBlocks.length)}
                      </span>
                      <select
                        disabled={!paid}
                        value={block.position}
                        onChange={(event) =>
                          run(
                            `text-${scene.id}-${index}`,
                            storyApi.updateScene(scene.id, {
                              textBlocks: scene.textBlocks.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, position: event.target.value as TextPosition }
                                  : item,
                              ),
                            }),
                          )
                        }
                      >
                        {allowedTextPositions(layout).map((position) => (
                          <option key={position} value={position}>
                            {t.story.manual.positions[position]}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })}

                {scene.variants.length > 1 ? (
                  <div>
                    <p className="mb-2 text-sm font-medium">{t.illustrations.variants}</p>
                    <div className="flex gap-2 overflow-x-auto">
                      {scene.variants.map((variant) => (
                        <button
                          key={variant.id}
                          type="button"
                          aria-pressed={scene.imageAssetId === variant.assetId}
                          className="shrink-0 rounded-lg border-2 p-1 aria-pressed:border-[var(--kitup-color-accent)]"
                          onClick={() =>
                            run(
                              `variant-${variant.id}`,
                              illustrationsApi.selectSceneVariant(scene.id, variant.id),
                            )
                          }
                        >
                          <img
                            src={variant.url}
                            alt={t.illustrations.variantAlt(scene.index + 1)}
                            className="h-20 w-28 rounded object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </li>
        ))}
        {/*
          The spreads the story already has, but the free preview does not draw: shown as
          locked places so a family sees what paying fills in, rather than one tile alone.
          Compact rows with each spread's words: a column of full-size padlocks said less in
          a phone's worth of scrolling than the story itself does in a line.
        */}
        {paid
          ? null
          : query.data.items.slice(visibleItems.length).map((scene) => {
              const words = scene.textBlocks
                .map((block) => block.text.trim())
                .filter(Boolean)
                .join(' ');
              return (
                <li
                  key={scene.id}
                  className="kitup-card flex items-start gap-3 border-dashed p-3 text-sm text-[var(--kitup-color-muted)]"
                >
                  <span aria-hidden="true" className="text-lg leading-5">
                    🔒
                  </span>
                  <span className="grid min-w-0 gap-0.5">
                    <span className="font-medium">
                      {t.monetization.preview.lockedSpread(scene.index + 1)}
                    </span>
                    {words ? <span className="line-clamp-2">{words}</span> : null}
                  </span>
                </li>
              );
            })}
      </ul>

      {paymentReady && !paymentDismissed ? (
        <PaymentModal
          bookId={book.id}
          previewUrl={
            preview?.variants.find((variant) => variant.assetId === preview.imageAssetId)?.url ??
            preview?.variants.at(-1)?.url ??
            null
          }
          previewAspect={preview ? spreadAspect(book.attributes, preview) : undefined}
          planId={book.plan}
          planName={t.monetization.plans[book.plan].name}
          remainingSpreads={Math.max(0, query.data.items.length - 1)}
          listAmountMinorUnits={brand.planPrices[book.plan]}
          pending={payment.isPending}
          error={payment.isError}
          onClose={() => setPaymentDismissed(true)}
          onPay={(promoCode) => payment.mutate(promoCode)}
        />
      ) : null}
      {replacementRequest ? (
        <ReplacementPackModal
          pending={replacementPack.isPending}
          error={replacementPack.isError}
          onClose={() => setReplacementRequest(null)}
          onPay={() => replacementPack.mutate()}
        />
      ) : null}
    </div>
  );
}

function PencilIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
    </svg>
  );
}

function PaymentModal({
  bookId,
  previewUrl,
  previewAspect,
  planId,
  planName,
  remainingSpreads,
  listAmountMinorUnits,
  pending,
  error,
  onClose,
  onPay,
}: {
  readonly bookId: string;
  /** The free spread itself: the dialog covers the page, so it shows what it announces. */
  readonly previewUrl: string | null;
  readonly previewAspect?: CSSProperties | undefined;
  readonly planId: BookPlanId;
  readonly planName: string;
  readonly remainingSpreads: number;
  readonly listAmountMinorUnits: number;
  readonly pending: boolean;
  readonly error: boolean;
  onClose(): void;
  onPay(promoCode?: string): void;
}): JSX.Element {
  const { t, locale } = useI18n();
  const brand = useBrand();
  const initialPromo =
    new URLSearchParams(globalThis.location.search).get('promo')?.toUpperCase() ?? '';
  const [promoCode, setPromoCode] = useState(initialPromo);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const promotion = useMutation({
    mutationFn: (code: string) => monetizationApi.quotePromotion(bookId, code),
  });
  const quotePromotion = promotion.mutate;
  useEffect(() => {
    if (initialPromo) quotePromotion(initialPromo);
  }, [initialPromo, quotePromotion]);
  const payableAmount = promotion.data?.amountMinorUnits ?? listAmountMinorUnits;
  const price = formatPrice(payableAmount, brand, locale);
  const free = payableAmount === 0;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/45 p-4"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-title"
        className="kitup-card max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-[var(--kitup-color-primary)]">{planName}</p>
            <h2 id="payment-title" className="mt-1 text-2xl font-semibold">
              {t.monetization.payment.title}
            </h2>
          </div>
          <button type="button" className="kitup-quiet-button" disabled={pending} onClick={onClose}>
            {t.common.close}
          </button>
        </div>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t.monetization.payment.previewAlt}
            className="mt-4 w-full rounded-[var(--kitup-radius-sm)] bg-slate-100 object-cover"
            style={previewAspect}
          />
        ) : null}
        <p className="mt-4 text-sm text-[var(--kitup-color-muted)]">
          {free
            ? t.monetization.payment.textFree(remainingSpreads, planName)
            : t.monetization.payment.text(remainingSpreads, planName)}
        </p>
        {/* The plan's name alone did not say what the price buys; the builder's own list does. */}
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer font-medium">{t.monetization.included}</summary>
          <ul className="mt-2 grid gap-1">
            {planFeatures(bookPlan(planId), t.monetization.features).map((feature) => (
              <li
                key={feature.text}
                className="kitup-plan-feature"
                data-included={String(feature.included)}
              >
                {feature.text}
              </li>
            ))}
          </ul>
        </details>
        {/* A code that makes it free leaves nothing to pay, in any currency. */}
        {free ? null : <p className="mt-4 text-sm font-medium">{t.monetization.payment.oneTime}</p>}
        {/*
          A brand that also charges another currency (by the buyer's country) shows it here:
          the button's price in the brand's currency, then a different amount at checkout,
          read as a price change.
        */}
        {(free ? [] : (brand.alternativePrices ?? [])).map((alternative) => (
          <p key={alternative.currency} className="mt-1 text-sm text-[var(--kitup-color-muted)]">
            {t.monetization.payment.alternativePrice(
              new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: alternative.currency,
                minimumFractionDigits: brand.priceFractionDigits,
                maximumFractionDigits: brand.priceFractionDigits,
              }).format(
                // Same rounding as the server's `discountedPrice`, so the amount is the charge.
                (Math.round(
                  (alternative.planPrices[planId] *
                    (100 - (promotion.data?.discountPercent ?? 0))) /
                    100 /
                    10 ** Math.max(0, 2 - brand.priceFractionDigits),
                ) *
                  10 ** Math.max(0, 2 - brand.priceFractionDigits)) /
                  100,
              ),
              alternative.currency,
            )}
          </p>
        ))}
        <div className="mt-4 grid gap-2">
          <label className="text-sm font-medium" htmlFor="payment-promo-code">
            {t.monetization.payment.promoLabel}
          </label>
          <div className="flex gap-2">
            <input
              id="payment-promo-code"
              className="min-w-0 flex-1 uppercase placeholder:normal-case"
              value={promoCode}
              placeholder={t.monetization.payment.promoPlaceholder}
              disabled={pending || promotion.isPending}
              onChange={(event) => {
                setPromoCode(event.target.value.toUpperCase());
                promotion.reset();
              }}
            />
            <button
              type="button"
              className="kitup-secondary-button"
              disabled={!promoCode.trim() || pending || promotion.isPending}
              onClick={() => promotion.mutate(promoCode)}
            >
              {t.monetization.payment.promoApply}
            </button>
          </div>
          {promotion.data ? (
            <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
              <p>{t.monetization.payment.promoApplied(promotion.data.discountPercent)}</p>
              <p className="mt-1">
                <s>{formatPrice(promotion.data.listAmountMinorUnits, brand, locale)}</s>{' '}
                <strong>{price}</strong>
              </p>
            </div>
          ) : null}
          {promotion.isError ? (
            <p role="alert" className="text-sm text-red-700">
              {t.monetization.payment.promoInvalid}
            </p>
          ) : null}
        </div>
        <div className="mt-4">
          <PaymentTerms checked={termsAccepted} disabled={pending} onChange={setTermsAccepted} />
        </div>
        <button
          type="button"
          className="kitup-primary-button mt-4 w-full justify-center"
          disabled={pending || !termsAccepted}
          onClick={() => onPay(promotion.data?.code)}
        >
          {pending
            ? free
              ? t.monetization.payment.freeProcessing
              : t.monetization.payment.processing
            : free
              ? t.monetization.payment.freeAction
              : t.monetization.payment.action(price)}
        </button>
        {error ? (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {t.monetization.payment.error}
          </p>
        ) : null}
      </section>
    </div>
  );
}

function ScenePreview({
  scene,
  aspect,
}: {
  scene: IllustrationSceneDto;
  aspect: CSSProperties;
}): JSX.Element {
  const { t } = useI18n();
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const selectedIndex = scene.variants.findIndex(
    (variant) => variant.assetId === scene.imageAssetId,
  );
  const fallbackIndex = scene.variants.length - 1;
  const previewIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;
  const selected = scene.variants[previewIndex];
  return selected ? (
    <>
      <button
        type="button"
        className="kitup-zoomable"
        aria-label={t.illustrations.zoom(scene.index + 1)}
        onClick={() => setViewerIndex(previewIndex)}
      >
        <img
          src={selected.url}
          alt={t.illustrations.previewAlt(scene.index + 1)}
          className="w-full bg-slate-100 object-cover"
          style={aspect}
        />
      </button>
      {viewerIndex !== null ? (
        <ImageViewer
          items={scene.variants.map((variant, index) => ({
            src: variant.url,
            alt: `${t.illustrations.variantAlt(scene.index + 1)} ${index + 1}`,
          }))}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      ) : null}
    </>
  ) : (
    <div
      className="grid place-items-center bg-slate-100 text-sm text-[var(--kitup-color-muted)]"
      style={aspect}
    >
      {t.illustrations.noPreview}
    </div>
  );
}
